// Added by Sejune Oh on 2019-02-18
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { createTheme, ThemeOptions } from '@material-ui/core/styles'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// # Login Form
export const LOGIN_FORM_WIDTH = 600
export const LOGIN_BANNER_HEIGHT = 47
export const LOGIN_BOX_HEIGHT = 403
export const LOGIN_BOX_PADDING = 24

// # Valid milliseconds for snackbar
export const SNACK_BAR_VALID_TIME = 3000

// # Drawer width
export const DRAWER_WIDTH = 180

// # Application header bar height
export const HEADER_BAR_HEIGHT = 47

// # Table Component's Default Row Height
export const TABLE_ROW_HEIGHT = 48

// # Table Component's Default Row Width
export const TABLE_ROW_WIDTH = 35

// # Mgmt Form Dialog Width
export const MGMT_FORM_DIALOG_WIDTH = 430

export const MGMT_LIST_MIN_WIDTH = 726
export const MGMT_LIST_MAX_WIDTH = 1200

export const MGMT_FORM_MIN_WIDTH = 0
export const MGMT_FORM_MAX_WIDTH = 726

export const LIGHT_GREY = 'rgba(255, 255, 255, 0.5)'
export const GREY_COLOR = '#424242'
export const BG_GREY = '#272b2e'
export const RED_COLOR = '#c04646'

export const DEFAULT_INPUT_VARIANT: 'standard' | 'outlined' | 'filled' = 'outlined'

export const DEFAULT_BTN_WIDTH = 160
export const DEFAULT_BTN_HEIGHT = 36

export const DEFAULT_SPACING = 12

// # Dashboard
export const DASHBOARD_CARD_SIZE = 330

// # Worklist
export const COLOR_BLUE = '#32a7d2'
export const COLOR_LIGHT_GREY = '#535353'
export const COLOR_LIGTH_NAVY = '#2c2d2f'
export const COLOR_DARK_NAVY = '#272b2e'
export const FONT_COLOR_TITLE = '#e6eaec'
export const FONT_COLOR_NORMAL = '#a5a5a5'
export const FONT_COLOR_DISABLED = '#646464'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * 20.02.18 - Masterail UI 쪽 Refactoring으로 Core와 Overrides 를 분리해서 운영하도록 합니다.
 * - Core : Material UI의 기본적인 스타일링을 유지함 + 필요한 부분에서 Custom하는 Component를 별도로 만들도록 함.
 * - Overrides : v1 에서 사용하던 방식 (Deprecated)
 */
const coreLineThemeBase: Partial<ThemeOptions> = {
  palette: {
    type: 'dark',
    primary: {
      light: '#437c9b',
      main: '#145c82',
      dark: '#0e405b',
      contrastText: '#fff',
    },
    secondary: {
      light: '#676767',
      main: GREY_COLOR,
      dark: ' #2e2e2e',
      contrastText: '#fff',
    },
    error: {
      light: '#ff2c2c',
      main: RED_COLOR,
      dark: '#7b1010',
      contrastText: '#fff',
    },
  },
  direction: 'ltr',
  spacing: DEFAULT_SPACING,
}
export const CoreLineThemeBase = () => createTheme({ ...coreLineThemeBase })

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const CoreLineTheme = createTheme({
  ...coreLineThemeBase,
  overrides: {
    MuiPaper: {
      root: {
        // border: '1px solid #000',
      },
      elevation1: {
        // boxShadow: 'none',
      },
      rounded: {
        // borderRadius: 0,
      },
    },
    MuiOutlinedInput: {
      input: {
        padding: DEFAULT_SPACING,
      },
      notchedOutline: {
        '&>legend': {
          width: '0 !important',
        },
      },
    },
    MuiButton: {
      root: {
        fontFamily: 'inherit',
        fontSize: '1.1rem',
        lineHeight: '2',
        minWidth: `${DEFAULT_BTN_WIDTH}px`,
        flexBasis: 'auto',
        width: 'auto',
        height: `${DEFAULT_BTN_HEIGHT}px`,
        padding: '0 16px'
      },
      label: {
        fontFamily: 'inherit'
      },
      contained: {},
    },
    MuiTable: {},
    MuiTableCell: {
      root: {
        fontFamily: 'inherit',
        fontSize: '1rem',
        padding: '10px',
        height: '50px'
      },
      footer: {
        borderBottom: 0
      },
    },
    MuiTableRow: {
      head: {
        height: '48px',
      },
    },
    MuiTab: {
      root: {
        fontFamily: 'inherit',
        '@media (min-width: 0px)': {
          fontSize: 'inherit',
        }
      }
    },
    MuiTypography: {
      root: {
        fontFamily: 'inherit',
        fontSize: '1rem'
      },
      h1: {
        fontFamily: 'inherit',
        fontSize: '1.625rem'
      },
      h2: {
        fontFamily: 'inherit',
        fontSize: '1.375rem'
      },
      h3: {
        fontFamily: 'inherit',
        fontSize: '1.25rem'
      },
      h4: {
        fontFamily: 'inherit',
        fontSize: '1.125rem'
      },
      h5: {
        fontFamily: 'inherit',
        fontSize: '1rem'
      },
      h6: {
        fontFamily: 'inherit',
        fontSize: '.875rem'
      },
      body1: {
        fontFamily: 'inherit',
        fontSize: '1rem'
      },
      body2: {
        fontFamily: 'inherit',
        fontSize: '1rem'
      }
    },
    MuiInputLabel: {
      root: {
        top: `-${DEFAULT_SPACING}px !important`,
        left: `-${DEFAULT_SPACING}px !important`,
        color: '#fff',
        fontWeight: 'bold',
        letterSpacing: '1px',
        '&$disabled': {
          color: 'rgba(255, 255, 255, 0.3) !important',
        },
        transfrom: 'none'
      },
    },
    MuiInputBase: {
      root: {
        fontFamily: 'inherit',
        fontSize: '1.1rem',
        '&$disabled': {
          color: 'rgba(255, 255, 255, 0.3) !important',
        }
      },
    },
    MuiSvgIcon: {
      root: {
        fontSize: '2rem'
      }
    },
    MuiFormLabel: {
      root: {
        fontFamily: 'inherit',
        '&$disabled': {
          color: 'rgba(255, 255, 255, 0.3) !important',
        },
      },
    },
    MuiIconButton: {
      label: {
        '&>input': {
          width: '0 !important',
          height: '0 !important',
          margin: '0 !important',
          padding: '0 !important',
          minWidth: '0 !important',
          minHeight: '0 !important',
          border: '0 !important',
        },
      },
    },
    MuiFormControl: {
      root: {
        boxSizing: 'border-box',
      },
    },
    MuiListItemIcon: {
      root: {
        minWidth: 'auto'
      }
    },
    MuiTablePagination: {
      toolbar: {
        overflow: 'hidden'
      }
    },
    MuiDialog: {
      root: {
        zIndex: 100
      }
    },
    MuiMenuItem: {
      root: {
        fontFamily: 'inherit'
      }
    },
    MuiFormHelperText: {
      root: {
        fontFamily: 'inherit',
        fontSize: '1rem'
      }
    },
    MuiSnackbarContent: {
      root: {
        fontFamily: 'inherit'
      }
    }
  },
})

export default CoreLineTheme
